import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { installScrollDepthTracking, trackPageview, trackWhatsappClick } from "@/lib/analytics";

/**
 * Mount once at app root. Installs:
 *  - SPA virtual pageviews (Yandex `hit`) on every route change
 *  - Scroll-depth goals (scroll_25/50/75/100), reset per route
 *  - Global delegated click tracking for any wa.me / api.whatsapp.com link.
 *    Source is enriched via `data-wa-source` attribute when present, otherwise
 *    inferred from the nearest landmark (header/footer/main/section id).
 */
export const AnalyticsTracker = () => {
  const location = useLocation();
  const cleanupRef = useRef<(() => void) | null>(null);
  const firstRenderRef = useRef(true);

  // Per-route: virtual pageview + reinstall scroll tracking
  useEffect(() => {
    // Skip the very first hit — Yandex auto-tracks the initial page load.
    if (!firstRenderRef.current) {
      trackPageview(window.location.href);
    }
    firstRenderRef.current = false;

    cleanupRef.current?.();
    cleanupRef.current = installScrollDepthTracking();
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [location.pathname, location.search]);

  // Global WhatsApp click delegation (mounted once)
  useEffect(() => {
    const inferSource = (link: HTMLAnchorElement): string => {
      if (link.dataset.waSource) return link.dataset.waSource;
      if (link.closest("header")) return "header";
      if (link.closest("footer")) return "footer";
      const section = link.closest("section[id]") as HTMLElement | null;
      if (section?.id) return `section:${section.id}`;
      if (link.closest("[data-hero]")) return "hero";
      return "link";
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (href.includes("wa.me/") || href.includes("api.whatsapp.com")) {
        trackWhatsappClick({
          source: inferSource(link),
          path: window.location.pathname,
        });
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
};
